import { DiceRoll, judge, Parser } from "../utils/dice";

// DiceRoll::DICE, in the order a notation lists them.
export const DICE = [2, 3, 4, 6, 8, 10, 12, 20, 100];
// As many of a die as the old dice bar let one add (its MAX_DIE_COUNTER), with no limit across the pool.
const MAX_PER_DIE = 1000;
const MAX_SIDES = 1000;
const MAX_NUMBER = 1000;
// What the server logs: DiceRoll's notation holds 64 characters and its result 255, in this format. A roll past them is
// still rolled and shown, just not shared (`storable`).
const MAX_NOTATION = 64;
const MAX_RESULT = 255;
const RESULT_FORMAT = /^[[\], 0-9+-]+$/;
// utils/dice.js's Parser rolls "d20++2" as a bare d20 and accepts "d20+" or "0d6".
const TERM = "(?:(?:[1-9]\\d*)?d[1-9]\\d*|\\d+)";
const FORMULA = new RegExp(`^${TERM}(?:[+-]${TERM})*$`);
const TERMS = /([+-]?)(?:(\d*)d(\d+)|(\d+))/g;

export type Roll = {
	notation: string;
	result: string;
	total: number;
	success: boolean | undefined;
	critical: "hit" | "miss" | undefined;
	dieIcon: number | null;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const signed = (value: number) => (value > 0 ? `+${value}` : String(value));
const normalize = (text: string) =>
	String(text ?? "")
		.replace(/\s+/g, "")
		.toLowerCase();

function terms(notation: string) {
	return [...notation.matchAll(TERMS)].map(([, sign, count, sides, number]) => ({
		negative: sign === "-",
		count: sides ? Number(count || 1) : 0,
		sides: Number(sides ?? 0),
		number: Number(number ?? 0),
	}));
}

export function createPool() {
	const counts = new Map<number, number>();
	let modifier = 0;

	const count = (die: number) => counts.get(die) ?? 0;
	const setModifier = (value: number) => {
		modifier = Number.isFinite(value) ? clamp(Math.trunc(value), -MAX_NUMBER, MAX_NUMBER) : 0;
	};
	const clear = () => {
		counts.clear();
		modifier = 0;
	};

	return {
		count,
		get modifier() {
			return modifier;
		},
		add(die: number) {
			if (DICE.includes(die) && count(die) < MAX_PER_DIE) counts.set(die, count(die) + 1);
		},
		remove(die: number) {
			if (count(die) > 1) counts.set(die, count(die) - 1);
			else counts.delete(die);
		},
		setModifier,
		step(delta: number) {
			setModifier(modifier + delta);
		},
		clear,
		// Empties the pool, then fills it from a formula made of table dice added
		// up and flat numbers; false when the formula is anything else.
		load(text: string) {
			clear();
			if (!normalize(text)) return true;
			const parsed = parse(text);
			const list = parsed.ok ? terms(parsed.notation) : [];
			const net = list.reduce((sum, term) => sum + (term.negative ? -term.number : term.number), 0);
			if (!parsed.ok || Math.abs(net) > MAX_NUMBER || list.some((term) => term.sides && (term.negative || !DICE.includes(term.sides)))) return false;
			for (const term of list) if (term.sides) counts.set(term.sides, count(term.sides) + term.count);
			if ([...counts.values()].some((value) => value > MAX_PER_DIE)) {
				clear();
				return false;
			}
			modifier = net;
			return true;
		},
		notation() {
			const dice = DICE.filter((die) => count(die) > 0)
				.map((die) => `${count(die) === 1 ? "" : count(die)}d${die}`)
				.join("+");
			return modifier ? `${dice}${signed(modifier)}` : dice;
		},
	};
}

export type Pool = ReturnType<typeof createPool>;

export function parse(text: string) {
	const notation = normalize(text);
	if (!FORMULA.test(notation) || !/d\d/.test(notation)) return { ok: false, notation };
	const list = terms(notation);
	if (list.some((term) => term.count > MAX_PER_DIE || term.sides > MAX_SIDES || term.number > MAX_NUMBER)) return { ok: false, notation };
	try {
		Parser.parse(notation);
		return { ok: true, notation };
	} catch {
		return { ok: false, notation };
	}
}

export function roll(notation: string, success?: string, attack = false): Roll {
	const dice = new DiceRoll(notation);
	const rolls = dice.rolls;
	const total = dice.total;
	const verdict = judge({ notation, rolls, total, success, attack });
	const sides = [...notation.matchAll(/d(\d+)/g)].map((match) => Number(match[1])).filter((die) => DICE.includes(die));

	return {
		notation,
		result: rolls.join(" "),
		total,
		success: verdict.success,
		critical: verdict.critical as Roll["critical"],
		dieIcon: sides.length ? Math.max(...sides) : null,
	};
}

// What the server's validations would take, the last check before a POST.
export function storable({ notation, result }: Roll) {
	return notation.length <= MAX_NOTATION && result.length <= MAX_RESULT && RESULT_FORMAT.test(result);
}

// Text the pool cannot hold is edited as text: a die is added to its end…
export function appendDie(text: string, die: number) {
	const notation = normalize(text);
	if (!notation) return `d${die}`;
	return /[+-]$/.test(notation) ? `${notation}d${die}` : `${notation}+d${die}`;
}

// …and the stepper moves its trailing number, adding one when there is none.
function trailing(text: string) {
	const notation = normalize(text).replace(/[+-]$/, "");
	const match = notation.match(/[+-]\d+$/);
	return { head: match ? notation.slice(0, match.index) : notation, value: match ? Number(match[0]) : 0 };
}

export const trailingNumber = (text: string) => trailing(text).value;

export function stepNumber(text: string, delta: number) {
	const { head, value } = trailing(text);
	const next = clamp(value + delta, -MAX_NUMBER, MAX_NUMBER);
	return next ? `${head}${signed(next)}` : head;
}
